import type { ModelProvider } from '../../../api/agent-miner-client/agent-miner-client.type';
import { TimeAllocationType } from '../../../api/work-periods-client/work-periods-client.type';

/** Tone bucket for the rework-rate value. Rework is LOWER-IS-BETTER. */
type ReworkTone = 'good' | 'neutral' | 'bad';

/**
 * Main-section view mode, cycled via the filter panel selector. `chart` and
 * `table` are the cycle-time chart and the all-metrics table; `usage` is the
 * token-usage view (its own chart/table toggle lives in the chart header).
 */
type ModelComparisonView = 'chart' | 'table' | 'usage';

/** Line/bar toggle for the cycle-time chart, held in the `cycle_view` param. */
type CycleView = 'line' | 'bar';

/** Display label per model provider for the filter panel and legend. */
const MODEL_PROVIDER_LABELS: Record<ModelProvider, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  xai: 'xAI',
  deepseek: 'DeepSeek',
  cursor: 'Cursor',
  unknown: 'Other',
};

/** Known provider values, derived from the label map so it can't drift out of sync. */
const MODEL_PROVIDER_VALUES = new Set<string>(Object.keys(MODEL_PROVIDER_LABELS));

/** Display label per allocation bucket for the sidebar Select. */
const ALLOCATION_TYPE_LABELS: Record<TimeAllocationType, string> = {
  [TimeAllocationType.Daily]: 'Daily',
  [TimeAllocationType.Weekly]: 'Weekly',
  [TimeAllocationType.BiWeekly]: 'Bi-Weekly',
  [TimeAllocationType.Monthly]: 'Monthly',
  [TimeAllocationType.Quarterly]: 'Quarterly',
};

/**
 * Narrows a wire-format provider string to a known {@link ModelProvider},
 * collapsing anything absent or unrecognized into "unknown"/"Other". Guards
 * against a backend that hasn't shipped the `provider` field yet (making it
 * `undefined` at runtime despite the type) and against future provider
 * values the frontend's union doesn't know about yet.
 *
 * The value is lowercased before matching: the known-value set holds lowercase
 * keys, so a capitalized backend value like `"Anthropic"` must be folded to
 * `"anthropic"` or it would silently collapse into "Other".
 *
 * @param {string | undefined} raw - The wire-format provider value, if present
 * @returns {ModelProvider} A known provider, defaulting to 'unknown'
 */
const normalizeProvider = (raw: string | undefined): ModelProvider => {
  const lowered = raw?.toLowerCase();
  return lowered && MODEL_PROVIDER_VALUES.has(lowered) ? (lowered as ModelProvider) : 'unknown';
};

/**
 * An inclusive date window (both bounds `YYYY-MM-DD`) scoping the sessions
 * drill-down. Present when the drill-down came from a chart dot, which
 * represents a single period rather than the whole selected range.
 */
interface SessionsDrilldownWindow {
  start: string;
  end: string;
}

export { ALLOCATION_TYPE_LABELS, MODEL_PROVIDER_LABELS, MODEL_PROVIDER_VALUES, normalizeProvider };
export type { CycleView, ModelComparisonView, ReworkTone, SessionsDrilldownWindow };
